// LLM-facing parameter schema for {@link TransformDatasetTool}.
//
// Pattern: `{ inputDataset, expression, outputDataset }`. The LLM
// writes a free-form s-expression that evaluates to a `Dataset`;
// `inputDataset` is informational (the dataset the LLM is operating
// on, looked up inside the expression via `(col <inputDataset>)`).
// The tool stages the result via `stageDatasetBinding`; the `<Tool>`
// execute boundary commits it as an immutable, persisted recipe only on
// a success envelope, so downstream s-expr tools can reference it by
// name (and a reload rehydrates it) while a failed call never persists
// one. Reusing an `outputDataset` name is a recoverable error —
// bindings are immutable.

import { z } from 'zod';

import { inputDatasetSchema, outputDatasetSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import type { DatasetSummary } from '#plugin-chat/lib/tool/dataset-summary.ts';

export const TransformDatasetInputSchema = z.object({
  inputDataset: inputDatasetSchema.describe(
    'Reference to the dataset to transform — must already be staged in chat history by an upstream producer.',
  ),
  expression: z
    .string()
    .describe(
      'An s-expression that evaluates to a Dataset. Typically wraps a single `(transform (col <inputDataset>) <verb> ...)` call; any expression returning a Dataset is accepted. The expression captures the operation declaratively — materialisation is deferred until a downstream consumer reads rows.',
    ),
  outputDataset: outputDatasetSchema,
});

/** @public */
export type TransformDatasetInput = z.infer<typeof TransformDatasetInputSchema>;

/**
 * Schema-summary envelope returned from the tool's `execute` to the LLM.
 *
 * Eval is eager (no I/O — verbs propagate the schema synchronously), so
 * the LLM receives the resolved column list + types immediately and can
 * chain further tool calls confidently. Materialised rows are fetched
 * lazily at consumption time (the tool's `render` calls
 * `useMaterializedExpr(args.expression)`).
 *
 * @public
 */
export type TransformDatasetOutput = Readonly<{
  /** Success discriminator — every tool success envelope carries this
   * (matches `ToolOutputSuccessSchema`); failures carry `success: false`
   * via the structured error envelope instead. */
  success: true;
  /** Discriminator (`'lazy' | 'materialized'`) from the resulting Dataset. */
  state: string;
  /** Echoed `inputDataset` from the args for round-trip clarity. */
  inputDataset: string;
  /** Echoed `outputDataset` — the binding under which the result was staged. */
  outputDataset: string;
  /** Column-list shape: `[ { name, kind }, … ]`. Matches the engine's
   * `Schema = ReadonlyMap<string, ColumnType>` after a serialisation pass. */
  schema: readonly Readonly<{ name: string; kind: string }>[];
  summary: DatasetSummary;
}>;
